import React from "react";
import dynamic from "next/dynamic";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import DtvMarketingAiModal from "src/components/video-lab/marketing-ai-modal";
import type { ModalType } from "lib/utils/modal-priority";
import { logError } from "lib/helper/logger";
import { runAnalyticsTask } from "lib/service/analytics-defer/analytics-defer";
import { normalizeFrequencyHours } from "lib/utils/exit-modal-frequency";
import { getSavedCart } from "src/utils/UtilityFunctions";

const DtvRevisitorModal = dynamic(
  () => import("../dtv-revisitor-modal")
);
const DtvExitModal = dynamic(
  () => import("src/components/video-lab/dtv-exit-modal")
);
const IdleTimerModal = dynamic(
  () => import("@directv-digital/dds.ui.idle-timer-modal")
);

/** Maximum modals shown per session before fatigue cap kicks in. */
const MAX_MODALS_PER_SESSION = 2;

/** B6: one-shot-per-pathname guard for the shared visit-counter increment. */
const visitCountedForPath: Record<string, boolean> = {};

/** Stable empty array ref — avoids child re-renders from inline `[]`. */
const EMPTY_DISABLE_PAGES: string[] = [];

// Generated by Copilot- Start - 2026-04-20 20:48:04
/**
 * WAT-10279: Client-side eligibility checks (cooldowns, session caps).
 * Global feature flags and page-level precedence are resolved at SSR —
 * by the time feeds reach the orchestrator, a non-null feed means
 * "this modal is eligible to compete."
 */
function isRevisitorEligible(revisitorFeed: any, hasCart: boolean): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  if (!revisitorFeed) {
    return false;
  }

  // Must have a saved cart with content (WAT-12195: build or satellite only)
  if (!hasCart) {
    return false;
  }

  // 48-hour cooldown (default 2880 minutes)
  const ts = window.localStorage.getItem("revisitorModalTimeStamp");
  if (ts) {
    const last = new Date(ts);
    if (!isNaN(last.getTime())) {
      const minutesSince = ((Date.now() - last.getTime()) / 36e5) * 60;
      const cooldownMinutes = revisitorFeed?.modalTimer ?? 2880;
      if (minutesSince <= cooldownMinutes) {
        return false;
      }
    }
  }

  return true;
}

function isExitModalEligible(exitFeed: any): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  if (!exitFeed) {
    return false;
  }

  // Already rendered this session
  if (window.sessionStorage.getItem("exitModalRendered") === "true") {
    return false;
  }

  // Frequency cooldown (default 24h). This cooldown deliberately outranks the
  // in-component visit window: a visitor who saw ANY exit modal within frequencyHours
  // does not get DtvExitModal mounted, even mid-window.
  //
  // SIGNED OFF — do not "fix" this asymmetry back. The WAT-11458 review raised that a
  // pzn/UTM feed inheriting this shared cross-feed clock narrows live campaign delivery
  // (a UTM impression now suppresses the regular modal and vice versa, so a campaign
  // shows less often than pre-unification). PO confirmed 2026-08-03: the behaviour is to
  // be IDENTICAL for both pipelines — that is the point of the unification, not a side
  // effect of it. So there is intentionally NO pzn exemption here and no per-pipeline
  // clock. A future reader tempted to special-case the pzn feed should ask the PO first.
  //
  // Consequence worth knowing: prod's UTM config on /genre-packs leaves frequencyHours
  // unauthored (verified live, cache-busted, 2026-07-29: onVisitNum "1", maxVisits "1",
  // frequencyHours unset), which the normalisation below resolves to 24h — so that
  // campaign moves from once-per-session to once-per-24h. No CMS re-authoring needed
  // for the signed-off behaviour.
  //
  // The SAME decision covers a REGULAR (non-pzn) feed with an unset frequencyHours, which
  // also had no cooldown before this PR and now has 24h. Base read `?? 24`, which does not
  // catch Storyblok's empty number field, so `""` survived and `hoursSince < ""` coerced to
  // `< 0` — never true. Base's own `?? 24` is the evidence that 24h was always the intended
  // default and the `""` leak was the defect. Confirmed 2026-08-05 as falling under the same
  // PO decision above: identical behaviour for both pipelines. The ticket's DoD wording
  // ("regular behaviour unchanged") is narrower than what shipped, deliberately — do NOT
  // special-case regular feeds back to no-cooldown to satisfy it, as that re-forks the two
  // pipelines on the very field this PR unified. Pinned by the regular-feed tests in
  // modal-orchestrator.spec.tsx ("regular (non-pzn) feed gets the same normalisation").
  const lastShown = window.localStorage.getItem("lastExitModalShownTime");
  if (lastShown) {
    // B3: normalise through the SHARED helper the component uses, never a second local
    // copy. `?? 24` would leave Storyblok's unset "" in place and `hoursSince < ""`
    // coerces to `< 0` — never true — so the cooldown this comment describes silently
    // would not exist for an unauthored field.
    const freq = normalizeFrequencyHours(exitFeed?.frequencyHours);
    const hoursSince = (Date.now() - parseInt(lastShown, 10)) / 36e5;
    // `freq <= 0` FIRST: a normalised 0 means "never recur", and testing only
    // `hoursSince < freq` would read it as the opposite — time never runs backwards, so
    // `hoursSince < 0` is never true and the cooldown would fall through to eligible,
    // re-showing an authored-once campaign on every new session.
    if (freq <= 0 || hoursSince < freq) {
      return false;
    }
  }

  return true;
}

function isMarketingAiEligible(maiFeed: any): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  if (!maiFeed) {
    return false;
  }

  // Already shown this session
  if (window.sessionStorage.getItem("purchaseLikelihoodModalShown") === "true") {
    return false;
  }

  return true;
}

/**
 * Given the sorted priority list and pre-computed eligibility flags,
 * return the first eligible modal type that hasn't been skipped.
 */
interface ResolveWinnerArgs {
  priorityList: ModalType[];
  exitEligible: boolean;
  revisitorEligible: boolean;
  maiEligible: boolean;
  modalDisplayCount: number;
  skippedModals: Set<ModalType>;
}

function resolveWinner({
  priorityList,
  exitEligible,
  revisitorEligible,
  maiEligible,
  modalDisplayCount,
  skippedModals
}: ResolveWinnerArgs): ModalType | null {
  for (const modalType of priorityList) {
    if (modalDisplayCount >= MAX_MODALS_PER_SESSION) {
      break;
    }
    if (skippedModals.has(modalType)) {
      continue;
    }

    if (modalType === "EXITMODALV2" && exitEligible) {
      return modalType;
    }
    if (modalType === "REVISITORMODAL" && revisitorEligible) {
      return modalType;
    }
    if (modalType === "MARKETINGAIMODAL" && maiEligible) {
      return modalType;
    }
  }
  return null;
}
// Generated by Copilot- End - 2026-04-20 20:48:04

export interface IModalOrchestratorProps {
  clientSideIsUserAuth?: boolean;
  /** Pre-resolved exit modal feed — SSR resolves page-level ?? (flag-gated global). Null = not eligible. */
  exitModalCMS?: any;
  globalFeaturesConfig?: any;
  idleTimerModal?: any;
  /** WAT-10279: MarketingAI (Purchase Likelihood) modal CMS segment data */
  marketingAiModalData?: any;
  /** WAT-10279: Sorted list of modal types in descending priority order */
  modalPriorityList?: ModalType[];
  /** Pre-resolved revisitor feed — SSR resolves page-level ?? (flag-gated global). Null = not eligible. */
  revisitorModalFeed?: any;
}

export function ModalOrchestrator(props: IModalOrchestratorProps) {
  try {
    const featuresConfig = props?.globalFeaturesConfig;
    const idleTimerFeed = props?.idleTimerModal;
    const clientSideIsUserAuth = props?.clientSideIsUserAuth;
    const disablePagesExitModal = featuresConfig?.disableExitModalFor;
    const disableIdleTimerModal = featuresConfig?.disableIdleTimerModal;
    const enableConfigureCart = featuresConfig?.enableConfigureCart ?? true;

    // Feeds are fully resolved at SSR: page-level ?? (flag-gated global).
    // A non-null feed here means "this modal is eligible to compete."
    const exitFeed = props?.exitModalCMS;
    const revisitorModalFeed = props?.revisitorModalFeed;
    const marketingAiFeed = props?.marketingAiModalData;

    // WAT-10279: Lazy-initialize from storage so the first render's
    // resolveWinner sees the real fatigue count / cart state. A 0-default
    // would make a modal briefly eligible before the effect corrected it.
    const [modalDisplayCount] = React.useState(() => (typeof window !== "undefined" ?
      parseInt(window.sessionStorage?.getItem("modalDisplayCount") ?? "0", 10) :
      0));
    const [hasCart] = React.useState(() => !!getSavedCart());
    // WAT-10279: Tracks modals that signalled they won't display
    // (e.g. traffic group B, no AI segment). When updated, the
    // orchestrator re-resolves to the next eligible priority.
    const [skippedModals, setSkippedModals] = React.useState<Set<ModalType>>(() => new Set());

    // WAT-10279: Centralized Session AI segment capture.
    // The orchestrator owns the listener (not MAI) so the buffer is alive
    // BEFORE any modal mounts — covers the priority-fallthrough case where
    // MAI mounts seconds after page load. The orchestrator ALSO owns the
    // GA dataLayer push so tracking captures every event regardless of
    // which modal (if any) ends up rendering.
    const [capturedSegment, setCapturedSegment] =
      React.useState<{ eppLabel: string; source: 'realtime' | 'localstorage' } | null>(null);

    React.useEffect(() => {
      if (typeof window === 'undefined') {
        return undefined;
      }

      // Initial: localStorage covers cross-page / cross-app captures
      // (z1 GTM tag writes z1_eppLabel synchronously before dispatching).
      try {
        const stored = window.localStorage.getItem('z1_eppLabel');
        if (stored && stored.length > 0) {
          setCapturedSegment({
            eppLabel: stored,
            source: 'localstorage'
          });
        }
      } catch { /* localStorage may be unavailable (private mode etc) */ }

      const handler = (e: Event) => {
        const detail = (e as CustomEvent)?.detail;
        if (!detail) {
          return;
        }

        // GA dataLayer push for EVERY captured event (tracking team parity).
        // dataLayer is queued before GTM loads — GTM processes it on load.
        try {
          const w = window as unknown as { dataLayer?: any[] };
          w.dataLayer = w.dataLayer || [];
          // Payload built synchronously (timestamp!); the push itself rides
          // the shared post-paint analytics queue.
          const payload = {
            event: 'sessionai_event_captured',
            eppLabel: detail.eppLabel || null,
            score: detail.score || null,
            page: window.location.pathname,
            timestamp: new Date().toISOString()
          };
          runAnalyticsTask(() => w.dataLayer?.push(payload));
        } catch { /* dataLayer push must never break the modal; a deferred
                     push failure is caught+logged inside the queue instead */ }

        // First-capture-wins for modal use; subsequent events still go to GA.
        if (typeof detail.eppLabel === 'string' && detail.eppLabel.length > 0) {
          setCapturedSegment(prev => prev ?? {
            eppLabel: detail.eppLabel,
            source: 'realtime'
          });
        }
      };

      window.addEventListener('z1_analytic', handler);
      return () => window.removeEventListener('z1_analytic', handler);
    }, []);

    // B6: advance the shared visit counter HERE, not inside DtvExitModal. The modal's
    // page-load effect was the only writer, so the counter only moved on visits where
    // the orchestrator actually mounted it — meaning `onVisitNum`/`maxVisits` counted
    // "mountable visits", not the visitor's real visits, and the drift never caught up.
    // The orchestrator mounts regardless of exit-modal eligibility, so this runs every
    // page load. One-shot per pathname, mirroring the modal's previous guard. Do NOT
    // use the userVisitCount COOKIE: it is seeded "2" and runs N+1, which makes
    // onVisitNum:"1" unreachable.
    //
    // A lazy useState initializer, NOT an effect — same reason as modalDisplayCount above.
    // DtvExitModal READS this counter to evaluate onVisitNum/maxVisits. React commits
    // effects bottom-up (child before parent), so an increment in a parent effect can be
    // observed post-hoc by a child that already read the pre-increment value in the same
    // commit — the visit window would then evaluate one visit behind on a warm navigation
    // where the modal's chunk is already cached. A state initializer runs during THIS
    // component's render, strictly before any child is created, so the child can never
    // see the stale value. The pathname guard keeps it idempotent under StrictMode's
    // double-invoked initializers.
    React.useState(() => {
      if (typeof window === "undefined") {
        return null;
      }
      const path = window.location?.pathname ?? "";
      if (visitCountedForPath[path]) {
        return null;
      }
      visitCountedForPath[path] = true;
      const parsed = parseInt(window.localStorage?.getItem("userVisitCount") ?? "0", 10);
      const current = Number.isFinite(parsed) ? parsed : 0;
      window.localStorage?.setItem("userVisitCount", `${current + 1}`);
      return null;
    });

    // WAT-10279: Callback for child modals to signal they won't show.
    // Triggers re-render → resolveWinner skips them → next priority wins.
    const handleSkip = React.useCallback((modalType: ModalType) => {
      setSkippedModals(prev => {
        if (prev.has(modalType)) {
          return prev;
        } // no-op, avoid infinite re-render
        const next = new Set(prev);
        next.add(modalType);
        return next;
      });
    }, []);

    // Generated by Copilot- Start - 2026-04-20 20:48:04
    const priorityList = props?.modalPriorityList;
    const hasPriorityConfig = priorityList && priorityList.length > 0;

    // Eligibility is simple — SSR already gated global flags.
    // Here we only check client-side conditions (cooldowns, session, cart).
    // B2: LATCHED at mount. isExitModalEligible reads the same two keys DtvExitModal
    // writes when it displays (exitModalRendered / lastExitModalShownTime), and this
    // component re-renders from its own z1_analytic listener and from a sibling's
    // onSkip — so re-deriving this would tear the open dialog out of the DOM the
    // instant it painted. Eligibility only ever degrades within a page load, so
    // latching loses nothing, and DtvExitModal keeps its own session + frequency
    // checks so a frozen mount decision cannot cause a re-show. Same reason
    // modalDisplayCount / hasCart are lazy-initialised above.
    const [exitEligible] = React.useState(() => isExitModalEligible(exitFeed));
    const revisitorEligible = isRevisitorEligible(revisitorModalFeed, hasCart);
    const maiEligible = isMarketingAiEligible(marketingAiFeed);

    // WAT-10279: Memoized onSkip callbacks — stable refs prevent child
    // useEffect re-fires when the orchestrator re-renders after skippedModals changes.
    const skipExit = React.useCallback(() => handleSkip("EXITMODALV2"), [handleSkip]);
    const skipRevisitor = React.useCallback(() => handleSkip("REVISITORMODAL"), [handleSkip]);
    const skipMAI = React.useCallback(() => handleSkip("MARKETINGAIMODAL"), [handleSkip]);

    // Resolve winner when priority is configured; null otherwise.
    const winner = hasPriorityConfig ?
      resolveWinner({
        priorityList,
        exitEligible,
        revisitorEligible,
        maiEligible,
        modalDisplayCount,
        skippedModals
      }) :
      null;
    const exitIsWinner = winner === "EXITMODALV2";
    const exitCanOpenOnPageLoad = !hasPriorityConfig || exitIsWinner;
    const exitInPriorityList = !hasPriorityConfig || priorityList.some(m => m === "EXITMODALV2");

    // A PZN/UTM-injected feed is just another exit-modal feed: it goes through the
    // same isExitModalEligible gate (once-per-session + frequency cooldown) as a
    // regular one. It used to bypass this because DtvExitModal kept a separate
    // per-pznKey gate; that fork is gone.
    const showExit = exitInPriorityList && exitEligible;
    const showRevisitor = revisitorEligible && (!hasPriorityConfig || winner === "REVISITORMODAL");
    const showMAI = maiEligible && (!hasPriorityConfig || winner === "MARKETINGAIMODAL");
    // Generated by Copilot- End - 2026-04-20 20:48:04

    return (
      <>
        {showExit && (
          <DtvExitModal
            disablePages={disablePagesExitModal || EMPTY_DISABLE_PAGES}
            enablePageLoadFlag={exitCanOpenOnPageLoad}
            exitModalData={exitFeed}
            onSkip={exitIsWinner ? skipExit : undefined}
          />
        )}
        {showRevisitor && (
          <DtvRevisitorModal
            enableConfigureCart={enableConfigureCart}
            revisitorModalFeed={revisitorModalFeed}
            onSkip={hasPriorityConfig ? skipRevisitor : undefined}
          />
        )}
        {showMAI && (
          <DtvMarketingAiModal
            capturedSegment={capturedSegment}
            modalData={marketingAiFeed}
            onSkip={hasPriorityConfig ? skipMAI : undefined}
          />
        )}

        {clientSideIsUserAuth && !disableIdleTimerModal && (
          <IdleTimerModal
            idleTimerModalCMS={idleTimerFeed}
            isLoggedIn={clientSideIsUserAuth}
          />
        )}
      </>
    );
  } catch (error) {
    // WAT-10279: Surface orchestrator failures instead of silently collapsing
    // every child modal into a single ErrorDisplay.
    logError("ModalOrchestrator", error);
    return <ErrorDisplay component="DTV Modal" error={error} />;
  }
}
